import {Component, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {Customer, TradingPartner, TradingPartnerFileExpectation, UpdateTradingPartnerRequest} from "@app/_models/api";
import {ActivatedRoute, RouterLink} from "@angular/router";
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {AsyncPipe} from "@angular/common";
import {GlobalService} from "@app/_services/global.service";
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {ApiService} from "@app/_services/api.service";
import {ToastService} from "@app/_services/toast.service";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {DaysOfWeek} from "@app/_models/global";
import {TradingPartnerService} from "@app/_services/master-data/trading-partners.service";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {TableSearchComponent} from "@app/_components/table-search/table-search.component";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

interface EditForm {
    id: FormControl<string | null>;
    name: FormControl<string | null>;
    abbreviation: FormControl<string | null>;
    groupName: FormControl<string | null>;
    address1: FormControl<string | null>;
    address2: FormControl<string | null>;
    city: FormControl<string | null>;
    state: FormControl<string | null>;
    zipCode: FormControl<string | null>;
    tradingPartnerFileExpectations: FormArray<FormGroup<FileExpectationForm>>;
}

interface FileExpectationForm {
    id: FormControl<string | null>;
    type: FormControl<string | ''>;
    dayOfWeek: FormControl<number>;
    fileCount: FormControl<number | 0>;
}

@Component({
    selector: 'app-trading-partners',
    templateUrl: './trading-partners.component.html',
    styleUrl: '../../app.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TableSearchComponent,
        TablePagerComponent,
        ReactiveFormsModule,
        AsyncPipe,
        ModalConfirmComponent,
        FormsModule,
        SortableDirective,
        RouterLink
    ]
})
export class TradingPartnersComponent implements OnDestroy {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChild('editModal') private editModal!: TemplateRef<ModalConfirmComponent>;
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;
    private onEditModalRef!: NgbModalRef
    private _customers$ = new BehaviorSubject<Customer[]>([]);
    private customersSubscription: Subscription;

    total$: Observable<number> | undefined;
    customers: Customer[] = [];
    customerId: string | undefined;
    savingData: boolean = false;

    editForm = new FormGroup<EditForm>({
        id: new FormControl("", Validators.required),
        name: new FormControl("", Validators.required),
        abbreviation: new FormControl("", Validators.required),
        groupName: new FormControl(""),
        address1: new FormControl("", Validators.required),
        address2: new FormControl(""),
        city: new FormControl("", Validators.required),
        state: new FormControl("", Validators.required),
        zipCode: new FormControl("", Validators.required),
        tradingPartnerFileExpectations: new FormArray<FormGroup<FileExpectationForm>>([])
    })

    constructor(
        public globalService: GlobalService,
        private route: ActivatedRoute,
        private toastService: ToastService,
        private modalService: NgbModal,
        private apiService: ApiService,
        public service: TradingPartnerService
    ){
        this._customers$.next(this.route.snapshot.data['resolvedData'].data);
        this.customersSubscription = this._customers$.subscribe(customers => {
            this.customers = customers;
        });
        this.total$ = this.service.total$;
    }

    ngOnDestroy() {
        this.customersSubscription.unsubscribe();
    }

    onCustomerChange(event: Event) {
        this.customerId = (event.target as HTMLInputElement).value;
        this.globalService.showWaitOverlay();

        this.apiService.getTradingPartners(this.customerId).subscribe({
            next: (tradingPartners: TradingPartner[]) => {
                this.service.data = tradingPartners;
                this.service.refreshDataSource();
                this.globalService.hideWaitOverlay();
            },
            error: () => {
                this.toastService.showDanger("Request Failed", "Failed to load trading partners. Please try again.");
                this.globalService.hideWaitOverlay();
            }
        });
    }

    getAddress(element: TradingPartner) {
        return `${element.address1} ${element.city}, ${element.state} ${element.zipCode}`;
    }

    onEdit(element: TradingPartner) {
        this.setupOnEditModalFormData(element);

        this.onEditModalRef = this.modalService.open(this.editModal, {
            modalDialogClass: "modal-dialog-centered",
            backdrop: "static",
        });
    }

    onEditDismiss() {
        this.modalConfirmComponent.confirmIfDirty(this.editForm, () => this.onEditModalRef.dismiss());
    }

    onSubmit() {
        if (this.editForm.invalid) {
            this.toastService.showDanger("Invalid Data", "Please fill-in all required fields.");
            return;
        }

        this.savingData = true;
        const tp = this.editForm.value;
        let expectations: TradingPartnerFileExpectation[] = [];

        // Get file expectations from the form array
        const fileExpectationsArray = this.editForm.controls.tradingPartnerFileExpectations;
        for (let i = 0; i < fileExpectationsArray.length; i++) {
            const formGroup = fileExpectationsArray.at(i);
            const expectation = formGroup.value;

            if (expectation.type && expectation.dayOfWeek !== null && expectation.fileCount !== null) {
                expectations.push({
                    id: expectation.id || null,
                    type: expectation.type,
                    dayOfWeek: expectation.dayOfWeek!,
                    fileCount: expectation.fileCount || 0
                });
            }
        }

        let body: UpdateTradingPartnerRequest = {
            name: tp.name!,
            abbreviation: tp.abbreviation!,
            groupName: tp.groupName ?? "",
            address1: tp.address1!,
            address2: tp.address2!,
            city: tp.city!,
            state: tp.state!,
            zipCode: tp.zipCode!,
            tradingPartnerFileExpectations: expectations
        };

        this.apiService.updateTradingPartner(this.customerId!, tp.id!, body).subscribe({
            next: (updated) => {
                this.toastService.showStandard("Success", "Trading Partner Successfully Updated");
                this.service.data = this.service.data.map(data => data.id == tp.id ? updated : data);

                this.service.refreshDataSource();
            },
            error: (e) => {
                this.toastService.showDanger("Request Failed", "Please try again.");
                this.savingData = false;
                throw e;
            },
            complete: () => {
                this.savingData = false;
                this.onEditModalRef.close();
            }
        });
    }

    createFileExpectationFormGroup(id: string | null = null, type: string = '', dayOfWeek: number | null = null, fileCount: number | null = null): FormGroup<FileExpectationForm> {
        return new FormGroup<FileExpectationForm>(<FileExpectationForm>{
            id: new FormControl(id),
            type: new FormControl(type),
            dayOfWeek: new FormControl(dayOfWeek),
            fileCount: new FormControl(fileCount)
        });
    }

    initializeFileExpectations(tradingPartnerId: string) {
        const fileExpectationsArray = this.editForm.controls.tradingPartnerFileExpectations;
        fileExpectationsArray.clear();

        // add 852 expectations for each day of the week
        for (let day = 1; day <= 7; day++) {
            fileExpectationsArray
                .push(this.createFileExpectationFormGroup(null, '852', day, 1));
        }

        // add 867 expectations for each day of the week
        for (let day = 1; day <= 7; day++) {
            fileExpectationsArray
                .push(this.createFileExpectationFormGroup(null, '867', day, 1));
        }
    }

    onSort({column, direction}: SortEvent) {
        // resetting other headers
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = '';
            }
        }

        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    get controls() {
        return this.editForm.controls;
    }

    get fileExpectationForm() {
        return this.editForm.controls.tradingPartnerFileExpectations.controls;
    }

    protected readonly DaysOfWeek = DaysOfWeek;

    private setupOnEditModalFormData(element: TradingPartner) {
        this.editForm.reset();
        this.editForm.patchValue({
            id: element.id,
            name: element.name,
            abbreviation: element.abbreviation,
            groupName: element.groupName ?? "",
            address1: element.address1,
            address2: element.address2,
            city: element.city,
            state: element.state,
            zipCode: element.zipCode,
        });

        this.initializeFileExpectations(element.id);

        if (!(element.tradingPartnerFileExpectations && element.tradingPartnerFileExpectations.length > 0)) {
            return;
        }

        const fileExpectationsArray = this.editForm.controls.tradingPartnerFileExpectations;
        element.tradingPartnerFileExpectations.forEach(expectation => {
            const index = fileExpectationsArray.controls.findIndex(control => {
                return control.controls.type.value === expectation.type &&
                    control.controls.dayOfWeek.value === expectation.dayOfWeek;
            });

            if (index === -1) {
                return;
            }

            const formGroup = fileExpectationsArray.at(index);
            formGroup.patchValue({
                id: expectation.id,
                type: expectation.type,
                dayOfWeek: expectation.dayOfWeek,
                fileCount: expectation.fileCount
            });
        });
    }
}
